import React from 'react';
import { usePageSEO } from '../hooks/usePageSEO';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import './About.css';
import './WhyWe.css';

const FLOW_STEPS = [
  'Idea',
  'Plan',
  'Design',
  'Develop',
  'Test',
  'Launch',
  'Market',
  'Sell',
  'Scale',
];

const OBJECTIVES = [
  'Deliver high-quality software products',
  'Align business goals with technical execution',
  'Ensure successful market entry and user adoption',
  'Enable scalability and long-term growth',
];

const PDLC_PHASES = [
  {
    id: '3.1',
    title: 'Ideation & Research',
    purpose: 'Identify problems and validate ideas',
    activities: [
      'Market research',
      'Competitor analysis',
      'User pain point identification',
      'Feasibility study',
    ],
    deliverables: ['Idea document', 'Market research report'],
  },
  {
    id: '3.2',
    title: 'Planning & Strategy',
    purpose: 'Define roadmap and business model',
    activities: [
      'Define target audience',
      'Create product roadmap',
      'Budget estimation',
      'Resource allocation',
    ],
    deliverables: ['Project plan', 'Timeline (Gantt chart)', 'Business model canvas'],
  },
  {
    id: '3.3',
    title: 'Requirement Analysis & Design',
    purpose: 'Convert idea into structured requirements',
    activities: [
      'Functional & non-functional requirements',
      'UI/UX design (wireframes, prototypes)',
      'System architecture design',
    ],
    deliverables: ['SRS (Software Requirement Specification)', 'Design mockups', 'Architecture diagrams'],
  },
  {
    id: '3.4',
    title: 'Development (SDLC Phase)',
    purpose: 'Build the product',
    activities: [
      'Frontend development',
      'Backend development',
      'API integration',
      'Database design',
    ],
    deliverables: ['Source code', 'Working modules'],
  },
  {
    id: '3.5',
    title: 'Testing & Quality Assurance',
    purpose: 'Ensure product quality and reliability',
    activities: [
      'Unit testing',
      'Integration testing',
      'System testing',
      'User acceptance testing (UAT)',
    ],
    deliverables: ['Test cases', 'Bug reports', 'QA reports'],
  },
  {
    id: '3.6',
    title: 'Deployment & Launch',
    purpose: 'Release product to users',
    activities: [
      'Server setup and deployment',
      'App store publishing',
      'Production release',
    ],
    deliverables: ['Live application', 'Deployment documentation'],
  },
  {
    id: '3.7',
    title: 'Marketing & Promotion',
    purpose: 'Create awareness and attract users',
    activities: [
      'Digital marketing (SEO, social media)',
      'Paid advertising (Google Ads, Meta Ads)',
      'Influencer marketing',
    ],
    deliverables: ['Marketing campaigns', 'Content strategy'],
  },
  {
    id: '3.8',
    title: 'Sales & User Acquisition',
    purpose: 'Convert users into customers',
    activities: [
      'Onboarding optimization',
      'Referral programs',
      'Discounts and offers',
    ],
    deliverables: ['Sales funnel', 'Customer acquisition reports'],
  },
  {
    id: '3.9',
    title: 'Maintenance & Scaling',
    purpose: 'Improve and expand product',
    activities: [
      'Bug fixing',
      'Feature updates',
      'Performance optimization',
      'Infrastructure scaling',
    ],
    deliverables: ['Updated versions', 'Performance reports'],
  },
];

const STAKEHOLDERS = [
  'Product Manager',
  'Developers (Frontend/Backend)',
  'UI/UX Designers',
  'QA Engineers',
  'Marketing Team',
  'Sales Team',
  'Customers',
];

const TOOLS = [
  { area: 'Frontend', items: 'Flutter, React' },
  { area: 'Backend', items: 'Node.js, Django' },
  { area: 'Database', items: 'MySQL, MongoDB' },
  { area: 'Cloud', items: 'AWS, Firebase' },
  { area: 'Marketing', items: 'Google Ads, Meta Ads, SEO Tools' },
];

const KEY_BENEFITS = [
  'Structured development approach',
  'Better risk management',
  'Faster time-to-market',
  'Improved product quality',
  'Business growth alignment',
];

function PdlcFlowSvg() {
  const w = 920;
  const h = 112;
  const boxW = 82;
  const boxH = 36;
  const gap = 14;
  const y = 38;
  let x = 24;
  const nodes = FLOW_STEPS.map((label, i) => {
    const bx = x;
    const el = (
      <g key={label}>
        <rect
          x={bx}
          y={y}
          width={boxW}
          height={boxH}
          rx="8"
          className="pdlc-flow-svg-rect"
        />
        <text
          x={bx + boxW / 2}
          y={y + boxH / 2 + 5}
          textAnchor="middle"
          className="pdlc-flow-svg-text"
        >
          {label}
        </text>
        {i < FLOW_STEPS.length - 1 && (
          <path
            d={`M ${bx + boxW + 2} ${y + boxH / 2} L ${bx + boxW + gap - 2} ${y + boxH / 2}`}
            className="pdlc-flow-svg-arrow"
            markerEnd="url(#pdlc-arrowhead)"
          />
        )}
      </g>
    );
    x += boxW + gap;
    return el;
  });

  return (
    <figure className="pdlc-figure pdlc-figure--svg">
      <figcaption className="pdlc-figure-caption">End-to-end flow (summary)</figcaption>
      <div className="pdlc-svg-scroll">
        <svg
          viewBox={`0 0 ${w} ${h}`}
          className="pdlc-flow-svg"
          role="img"
          aria-label="Flow from Idea through Plan, Design, Develop, Test, Launch, Market, Sell, to Scale"
        >
          <defs>
            <marker
              id="pdlc-arrowhead"
              markerWidth="8"
              markerHeight="8"
              refX="6"
              refY="4"
              orient="auto"
            >
              <polygon points="0 0, 8 4, 0 8" className="pdlc-flow-svg-marker" />
            </marker>
            <linearGradient id="pdlc-box-grad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#f8fafc" />
              <stop offset="100%" stopColor="#e2e8f0" />
            </linearGradient>
          </defs>
          {nodes}
        </svg>
      </div>
    </figure>
  );
}

function PdlcLifecycleSvg() {
  return (
    <figure className="pdlc-figure pdlc-figure--lifecycle">
      <figcaption className="pdlc-figure-caption">
        Lifecycle view: strategy, build, and growth
      </figcaption>
      <svg
        viewBox="0 0 640 280"
        className="pdlc-lifecycle-svg"
        role="img"
        aria-label="Three swimlanes: Strategy phases, Product build and QA, Go-to-market and scale"
      >
        <defs>
          <linearGradient id="pdlc-lane-grad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#eff6ff" />
            <stop offset="100%" stopColor="#dbeafe" />
          </linearGradient>
          <marker
            id="pdlc-down"
            markerWidth="7"
            markerHeight="7"
            refX="3.5"
            refY="7"
            orient="auto"
          >
            <polygon points="0 0, 7 3.5, 0 7" fill="#64748b" />
          </marker>
        </defs>

        <text x="24" y="28" className="pdlc-lifecycle-title">
          {'Strategy & discovery'}
        </text>
        <rect x="20" y="36" width="600" height="56" rx="10" fill="url(#pdlc-lane-grad)" stroke="#bfdbfe" />
        <text x="40" y="62" className="pdlc-lifecycle-lane-text">
          {'Ideation → Planning → Requirements & design'}
        </text>
        <text x="40" y="82" className="pdlc-lifecycle-lane-sub">
          Validate the problem, shape the roadmap, specify and design the solution
        </text>

        <text x="24" y="118" className="pdlc-lifecycle-title">
          Build, quality & release
        </text>
        <rect x="20" y="126" width="600" height="56" rx="10" fill="#f0fdf4" stroke="#bbf7d0" />
        <text x="40" y="152" className="pdlc-lifecycle-lane-text">
          {'Development → Testing → Deployment & launch'}
        </text>
        <text x="40" y="172" className="pdlc-lifecycle-lane-sub">
          Ship reliable software with QA gates and production-ready delivery
        </text>

        <text x="24" y="208" className="pdlc-lifecycle-title">
          Go-to-market & continuous improvement
        </text>
        <rect x="20" y="216" width="600" height="56" rx="10" fill="#faf5ff" stroke="#e9d5ff" />
        <text x="40" y="242" className="pdlc-lifecycle-lane-text">
          {'Marketing → Sales & acquisition → Maintenance & scaling'}
        </text>
        <text x="40" y="262" className="pdlc-lifecycle-lane-sub">
          Drive adoption, revenue, and long-term product health
        </text>

        <path
          d="M 320 92 L 320 124"
          className="pdlc-lifecycle-connector"
          markerEnd="url(#pdlc-down)"
        />
        <path
          d="M 320 182 L 320 214"
          className="pdlc-lifecycle-connector"
          markerEnd="url(#pdlc-down)"
        />
      </svg>
    </figure>
  );
}

const WhyWe = () => {
  const [introRef, introVisible] = useScrollAnimation(0.12);
  const [phasesRef, phasesVisible] = useScrollAnimation(0.08);
  const [pillarsRef, pillarsVisible] = useScrollAnimation(0.1);

  usePageSEO(
    'What Makes Us Different | PDLC & How We Work - GoToBusiness',
    'End-to-end PDLC: ideation, planning, design, development, testing, launch, marketing, sales, and scaling. How we align product delivery with business outcomes.'
  );

  return (
    <div className="about why-we">
      <section className="about-hero">
        <div className="container">
          <h1>Why We</h1>
          <p>Project / product development life cycle — from idea to scale</p>
        </div>
      </section>

      <section
        ref={introRef}
        className={`company-story section pdlc-intro ${introVisible ? 'animate' : ''}`}
      >
        <div className="container">
          <div className="story-content pdlc-doc">
            <h2>Project / Product Development Life Cycle (End-to-End)</h2>
            <h3 className="pdlc-h3">1. Introduction</h3>
            <p>
              The Project Development Life Cycle (PDLC) defines the complete journey of a product
              from idea generation to market launch and continuous improvement. It combines
              technical development (SDLC) with business strategy, marketing, and growth.
            </p>

            <h3 className="pdlc-h3">2. Objectives</h3>
            <ul className="pdlc-list">
              {OBJECTIVES.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>

            <h3 className="pdlc-h3 pdlc-h3--diagram">3. Life cycle phases</h3>
            <p className="pdlc-lead">
              Nine phases below mirror how we think about delivery with clients: discovery and
              design, engineering and quality, then launch, growth, and iteration.
            </p>

            <div className="pdlc-diagrams">
              <div className="pdlc-flow-html" aria-hidden="false">
                <span className="pdlc-flow-html-label">Simple flow (readable on all screens)</span>
                <div className="pdlc-flow-line">
                  {FLOW_STEPS.map((step, index) => (
                    <React.Fragment key={step}>
                      <span className="pdlc-flow-node">{step}</span>
                      {index < FLOW_STEPS.length - 1 && (
                        <span className="pdlc-flow-arrow" aria-hidden="true">
                          →
                        </span>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
              <PdlcFlowSvg />
              <PdlcLifecycleSvg />
            </div>
          </div>
        </div>
      </section>

      <section
        ref={phasesRef}
        className={`section pdlc-phases-section ${phasesVisible ? 'animate' : ''}`}
        style={{ background: '#f8f9fa' }}
      >
        <div className="container">
          <h2 className="pdlc-section-title text-center">Phase detail</h2>
          <p className="pdlc-section-sub text-center">
            Purpose, activities, and deliverables for each stage
          </p>
          <div className="pdlc-phase-grid">
            {PDLC_PHASES.map((phase) => (
              <article key={phase.id} className="card pdlc-phase-card">
                <div className="pdlc-phase-id">{phase.id}</div>
                <h3 className="pdlc-phase-title">{phase.title}</h3>
                <p className="pdlc-phase-purpose">
                  <strong>Purpose:</strong> {phase.purpose}
                </p>
                <h4 className="pdlc-phase-subh">Activities</h4>
                <ul className="pdlc-phase-ul">
                  {phase.activities.map((a) => (
                    <li key={a}>{a}</li>
                  ))}
                </ul>
                <h4 className="pdlc-phase-subh">Deliverables</h4>
                <ul className="pdlc-phase-ul pdlc-phase-ul--deliverables">
                  {phase.deliverables.map((d) => (
                    <li key={d}>{d}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section pdlc-meta-section">
        <div className="container">
          <div className="grid grid-2 pdlc-meta-grid">
            <div className="card pdlc-meta-card">
              <h3 className="pdlc-h3">4. Key stakeholders</h3>
              <ul className="pdlc-list pdlc-list--compact">
                {STAKEHOLDERS.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            </div>
            <div className="card pdlc-meta-card">
              <h3 className="pdlc-h3">5. Tools &amp; technologies (example)</h3>
              <ul className="pdlc-tools-list">
                {TOOLS.map((t) => (
                  <li key={t.area}>
                    <strong>{t.area}:</strong> {t.items}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="card pdlc-benefits-card">
            <h3 className="pdlc-h3">6. Key benefits</h3>
            <ul className="pdlc-list">
              {KEY_BENEFITS.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          </div>

          <div className="story-content pdlc-doc pdlc-conclusion">
            <h3 className="pdlc-h3">7. Conclusion</h3>
            <p>
              The Project Development Life Cycle ensures that a product is not only developed
              efficiently but also successfully launched and sustained in the market. It bridges the
              gap between technology and business strategy.
            </p>
            <h3 className="pdlc-h3">8. Simple flow summary</h3>
            <p className="pdlc-flow-summary-text">
              Idea → Plan → Design → Develop → Test → Launch → Market → Sell → Scale
            </p>
          </div>
        </div>
      </section>

      <section
        ref={pillarsRef}
        className={`why-we-pillars section ${pillarsVisible ? 'animate' : ''}`}
        style={{
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        }}
      >
        <div className="container">
          <h2 className="gradient-text text-center">What you can expect</h2>
          <div className="grid grid-3">
            <div className="card">
              <h3 className="why-we-pillar-title">Clarity first</h3>
              <p className="why-we-pillar-text">
                Straightforward proposals, visible progress, and no surprise scope creep without
                your sign-off.
              </p>
            </div>
            <div className="card">
              <h3 className="why-we-pillar-title">Engineering discipline</h3>
              <p className="why-we-pillar-text">
                Structured reviews, sensible defaults for security and performance, and builds that
                are ready for real users.
              </p>
            </div>
            <div className="card">
              <h3 className="why-we-pillar-title">Partnership mindset</h3>
              <p className="why-we-pillar-text">
                We treat your roadmap as shared ground: proactive suggestions, honest trade-offs,
                and support after go-live.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyWe;
